import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useApi } from '../hooks/useApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

/**
 * Open a LinkedIn profile URL robustly across in-app webviews.
 *
 * LinkedIn sends `X-Frame-Options: DENY`, so it refuses to render inside any
 * preview webview (Emergent preview app, Gmail in-app browser, etc.) — the
 * user sees a black "Failed to load" screen. This helper:
 *   1. Tries `window.open(url, '_blank')` first (works in real browsers).
 *   2. If the webview blocks it (returns null), falls back to copying the
 *      URL to the clipboard and showing a toast so the user can paste it
 *      into their real browser.
 */
function openLinkedInProfile(rawUrl) {
  if (!rawUrl) return;
  const url = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;
  let win = null;
  try {
    win = window.open(url, '_blank', 'noopener,noreferrer');
  } catch (e) {
    win = null;
  }
  if (win) {
    return; // success — opened in a new tab
  }
  // Webview blocked the popup. Copy the URL so the user can paste it into
  // their real browser app.
  const showCopiedToast = () => {
    toast.success('LinkedIn link copied', {
      description: 'Paste into your browser — LinkedIn blocks in-app previews.',
      duration: 7000,
    });
  };
  if (navigator?.clipboard?.writeText) {
    navigator.clipboard
      .writeText(url)
      .then(showCopiedToast)
      .catch(() => {
        toast.error('Could not open LinkedIn here', {
          description: `Open this URL in your browser: ${url}`,
          duration: 10000,
        });
      });
  } else {
    toast.error('Could not open LinkedIn here', {
      description: `Open this URL in your browser: ${url}`,
      duration: 10000,
    });
  }
}
import { 
  Search, 
  Users, 
  Building2, 
  Linkedin, 
  ExternalLink,
  UserPlus,
  Loader2,
  MapPin,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Target,
  TrendingUp,
  Globe,
  Check,
  Mail,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Confidence badge colors
const CONFIDENCE_COLORS = {
  high: 'bg-green-100 text-green-800 border-green-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200'
};

export default function DecisionMakerDiscovery() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const api = useApi();
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [discoveredContacts, setDiscoveredContacts] = useState([]);
  const [searchMetadata, setSearchMetadata] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [selectedOpps, setSelectedOpps] = useState([]);
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [suggestedContacts, setSuggestedContacts] = useState([]);
  const [loadingSuggested, setLoadingSuggested] = useState(true);
  const [addingContact, setAddingContact] = useState(null); // Contact being added
  const [creatingAccount, setCreatingAccount] = useState(false); // Creating new opp inline
  const [selectedAccount, setSelectedAccount] = useState(''); // Selected account for adding
  const [autoSearched, setAutoSearched] = useState(false);
  // Per-row email reveal state: { [idx]: { email, revealed, loading } }
  const [revealedEmails, setRevealedEmails] = useState({});

  async function handleRevealEmail(idx, contact) {
    const url = contact?.linkedin_url;
    if (!url) {
      toast.error('No LinkedIn URL to reveal from');
      return;
    }
    setRevealedEmails((s) => ({ ...s, [idx]: { ...(s[idx] || {}), loading: true } }));
    try {
      const res = await api.post('/prospects/enrich', { linkedin_url: url });
      const email = res?.person?.email || null;
      setRevealedEmails((s) => ({
        ...s,
        [idx]: { email, revealed: true, loading: false },
      }));
      toast.success(email ? '1 intelligence credit used' : 'Email not found');
    } catch (err) {
      setRevealedEmails((s) => ({ ...s, [idx]: { ...(s[idx] || {}), loading: false } }));
      toast.error('Email reveal failed — try again');
    }
  }
  
  useEffect(() => {
    fetchOpportunities();
    fetchSuggestedContacts();
  }, []);
  
  // Auto-search if company param is present in URL
  useEffect(() => {
    const companyParam = searchParams.get('company');
    const locationParam = searchParams.get('location');
    
    if (companyParam && !autoSearched) {
      setSearchTerm(companyParam);
      if (locationParam) setLocation(locationParam);
      setAutoSearched(true);
      // Trigger search after state update
      setTimeout(() => {
        handleSearchWithParams(companyParam, locationParam || '');
      }, 100);
    }
  }, [searchParams, autoSearched]);
  
  const handleSearchWithParams = async (company, loc) => {
    if (!company.trim()) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/intelligence/discover-decision-makers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_name: company,
          location: loc || undefined
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setDiscoveredContacts(data.discovered_contacts || []);
        setSearchMetadata(data);
        
        if (data.total_discovered === 0) {
          toast.info('No decision makers found. Try a different search term.');
        } else {
          toast.success(`Found ${data.total_discovered} decision makers!`);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Discovery failed');
      }
    } catch (err) {
      toast.error('Error running discovery');
    } finally {
      setLoading(false);
    }
  };
  
  const getToken = () => localStorage.getItem('token');
  
  const fetchOpportunities = async () => {
    try {
      const res = await fetch(`${API}/api/opportunities`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setOpportunities(data.filter(o => !['closed_won', 'closed_lost'].includes(o.stage)));
      }
    } catch (err) {
      console.error('Error fetching opportunities:', err);
    }
  };
  
  const fetchSuggestedContacts = async () => {
    try {
      const res = await fetch(`${API}/api/discovery/suggested-contacts`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestedContacts(data);
      }
    } catch (err) {
      console.error('Error fetching suggested contacts:', err);
    } finally {
      setLoadingSuggested(false);
    }
  };
  
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a company name');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/intelligence/discover-decision-makers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          company_name: searchTerm,
          location: location || undefined
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setDiscoveredContacts(data.discovered_contacts || []);
        setSearchMetadata(data);
        
        if (data.total_discovered === 0) {
          toast.info('No decision makers found. Try a different search term.');
        } else {
          toast.success(`Found ${data.total_discovered} decision makers!`);
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Discovery failed');
      }
    } catch (err) {
      toast.error('Error running discovery');
    } finally {
      setLoading(false);
    }
  };
  
  const handleBulkDiscovery = async () => {
    if (selectedOpps.length === 0) {
      toast.error('Select at least one opportunity');
      return;
    }
    
    setBulkLoading(true);
    try {
      const res = await fetch(`${API}/api/intelligence/pipeline-discovery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ opportunity_ids: selectedOpps })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Discovered ${data.total_discovered} contacts across ${data.companies_processed} companies!`);
        setSelectedOpps([]);
        fetchSuggestedContacts(); // Refresh suggested contacts
      } else {
        toast.error('Bulk discovery failed');
      }
    } catch (err) {
      toast.error('Error running bulk discovery');
    } finally {
      setBulkLoading(false);
    }
  };
  
  const handleConvertContact = async (contact) => {
    try {
      const res = await fetch(`${API}/api/discovery/suggested-contacts/${contact.id}/convert`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        toast.success(`${contact.name} added to CRM!`);
        setSuggestedContacts(prev => prev.filter(c => c.id !== contact.id));
      } else {
        toast.error('Failed to convert contact');
      }
    } catch (err) {
      toast.error('Error converting contact');
    }
  };
  
  const handleDismissContact = async (contactId) => {
    try {
      const res = await fetch(`${API}/api/discovery/suggested-contacts/${contactId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        setSuggestedContacts(prev => prev.filter(c => c.id !== contactId));
        toast.success('Contact dismissed');
      }
    } catch (err) {
      toast.error('Error dismissing contact');
    }
  };

  // Inline "+ Create new account" — used when the prospect company has no
  // existing opportunity yet. Creates a fresh opportunity in `prospecting`
  // stage and selects it, then the existing Add Contact flow runs.
  const handleCreateAccountInline = async () => {
    if (!addingContact) return;
    const companyName = (addingContact.company || searchTerm || '').trim();
    if (!companyName) {
      toast.error('No company name to create an account from');
      return;
    }
    setCreatingAccount(true);
    try {
      const res = await fetch(`${API}/api/opportunities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company_name: companyName,
          contact_name: addingContact.name || '',
          contact_title: addingContact.title || null,
          property_type: 'Hotel',
          deal_value: 0,
          probability: 10,
          source: 'decision_maker_discovery',
          notes: `Auto-created from Decision Maker Discovery while adding ${addingContact.name || 'a contact'}.`,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create account');
        return;
      }
      const newOpp = await res.json();
      // Refresh local opp list + select the new one so the existing
      // Add Contact button is now enabled.
      setOpportunities((prev) => [newOpp, ...prev]);
      setSelectedAccount(newOpp.id);
      toast.success(`Created account "${companyName}" — ready to add contact`);
    } catch (err) {
      toast.error('Error creating account');
    } finally {
      setCreatingAccount(false);
    }
  };

  // Handler to add discovered contact to CRM
  const handleAddDiscoveredContact = async () => {
    if (!addingContact || !selectedAccount) {
      toast.error('Please select an account');
      return;
    }

    try {
      // Parse the name into first and last name
      const nameParts = addingContact.name?.split(' ') || ['Unknown'];
      const firstName = nameParts[0] || 'Unknown';
      const lastName = nameParts.slice(1).join(' ') || '';
      const contactEmail = addingContact.email || `${firstName.toLowerCase()}.${lastName.toLowerCase().replace(/\s/g, '')}@placeholder.com`;

      // Step 1: Create the contact
      const res = await fetch(`${API}/api/contacts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email: contactEmail,
          account_id: selectedAccount,
          title: addingContact.title || '',
          phone: '',
          contact_type_tags: ['Decision Maker'],
          consent_status: 'unknown',
          notes: `Discovered via LinkedIn search. Company: ${addingContact.company || searchTerm}. LinkedIn: ${addingContact.linkedin_url || 'N/A'}`,
          linkedin_url: addingContact.linkedin_url || null
        })
      });

      if (res.ok) {
        const newContact = await res.json();
        
        // Step 2: Update the opportunity to set this as primary contact
        const updateRes = await fetch(`${API}/api/opportunities/${selectedAccount}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            primary_contact_id: newContact.id,
            contact_name: `${firstName} ${lastName}`.trim(),
            contact_email: contactEmail
          })
        });

        if (updateRes.ok) {
          toast.success(`${addingContact.name} added and linked to opportunity!`);
        } else {
          toast.success(`${addingContact.name} added to CRM!`);
        }
        
        // Remove from discovered contacts list
        setDiscoveredContacts(prev => prev.filter(c => c.name !== addingContact.name));
        setAddingContact(null);
        setSelectedAccount('');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add contact');
      }
    } catch (err) {
      toast.error('Error adding contact to CRM');
    }
  };
  
  const toggleOppSelection = (oppId) => {
    setSelectedOpps(prev => 
      prev.includes(oppId) 
        ? prev.filter(id => id !== oppId)
        : [...prev, oppId]
    );
  };

  return (
    <div className="min-h-screen bg-[#F7F5F2]">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-[#1C1C1C] tracking-tight">
            Decision Maker Intelligence
          </h1>
          <p className="text-[#6B6B6B] mt-1">
            Discover executives and decision makers at hospitality properties
          </p>
        </div>
        
        {/* Search Section */}
        <Card className="mb-8 border-[#E8E4DE] bg-white shadow-sm">
          <CardHeader className="border-b border-[#E8E4DE]">
            <CardTitle className="flex items-center gap-2 text-lg font-medium">
              <Search className="w-5 h-5 text-[#B89B5E]" />
              Company Search
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Enter company name (e.g., Fairmont Whistler)"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-[#E8E4DE]"
                  data-testid="company-search-input"
                />
              </div>
              <div className="w-full md:w-64">
                <Input
                  placeholder="Location (optional)"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="border-[#E8E4DE]"
                  data-testid="location-input"
                />
              </div>
              <Button 
                onClick={handleSearch}
                disabled={loading}
                className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                data-testid="discover-button"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Discover
              </Button>
            </div>
            
            {searchMetadata && (
              <div className="mt-4 flex items-center gap-4 text-sm text-[#6B6B6B]">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {searchMetadata.total_discovered} found
                </span>
                <span className="flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  {searchMetadata.high_confidence_count} high confidence
                </span>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Discovery Results */}
        {discoveredContacts.length > 0 && (
          <Card className="mb-8 border-[#E8E4DE] bg-white shadow-sm">
            <CardHeader className="border-b border-[#E8E4DE]">
              <CardTitle className="flex items-center gap-2 text-lg font-medium">
                <Users className="w-5 h-5 text-[#B89B5E]" />
                Discovery Results for "{searchTerm}"
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-[#E8E4DE]">
                {discoveredContacts.map((contact, idx) => (
                  <div 
                    key={idx}
                    className="p-4 hover:bg-[#FAF9F7] transition-colors flex items-center justify-between"
                    data-testid={`discovered-contact-${idx}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-[#F0EDE8] flex items-center justify-center">
                        <span className="text-lg font-medium text-[#B89B5E]">
                          {contact.name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-medium text-[#1C1C1C]">{contact.name}</h4>
                        <p className="text-sm text-[#6B6B6B]">{contact.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-xs ${CONFIDENCE_COLORS[contact.confidence] || CONFIDENCE_COLORS.medium}`}>
                            {contact.confidence_score}% confidence
                          </Badge>
                          {contact.is_primary_target && (
                            <Badge className="bg-[#B89B5E]/10 text-[#B89B5E] border-[#B89B5E]/20 text-xs">
                              Primary Target
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(() => {
                        const r = revealedEmails[idx] || {};
                        if (r.revealed) {
                          return (
                            <span
                              className="text-xs font-mono text-[#1A1A1A] px-2 py-1 bg-[#FAF4E6] border border-[#B89B5E]/30 rounded-sm"
                              data-testid={`dm-revealed-email-${idx}`}
                            >
                              {r.email || <span className="italic text-muted-foreground">Email not found</span>}
                            </span>
                          );
                        }
                        return (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#B89B5E] text-[#8A6F2E] hover:bg-[#FAF4E6]"
                            onClick={() => handleRevealEmail(idx, contact)}
                            disabled={r.loading || !contact.linkedin_url}
                            title={contact.linkedin_url ? 'Reveal email (1 intelligence credit)' : 'LinkedIn URL required to reveal'}
                            data-testid={`reveal-email-btn-${idx}`}
                          >
                            {r.loading ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <Mail className="w-3.5 h-3.5 mr-1" />
                            )}
                            Reveal Email <span className="ml-1 text-xs">(1 credit)</span>
                          </Button>
                        );
                      })()}
                      {contact.linkedin_url && (
                        <a 
                          href={contact.linkedin_url.startsWith('http') ? contact.linkedin_url : `https://${contact.linkedin_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg hover:bg-[#E8E4DE] transition-colors"
                        >
                          <Linkedin className="w-5 h-5 text-[#0077B5]" />
                        </a>
                      )}
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="border-[#B89B5E] text-[#B89B5E] hover:bg-[#B89B5E]/10"
                        onClick={() => setAddingContact(contact)}
                        data-testid={`add-to-crm-btn-${idx}`}
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        Add to CRM
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Bulk Discovery Panel */}
        <Card className="mb-8 border-[#E8E4DE] bg-white shadow-sm">
          <CardHeader 
            className="border-b border-[#E8E4DE] cursor-pointer"
            onClick={() => setShowBulkPanel(!showBulkPanel)}
          >
            <CardTitle className="flex items-center justify-between text-lg font-medium">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#B89B5E]" />
                Pipeline Discovery
                <Badge variant="outline" className="ml-2">
                  {selectedOpps.length} selected
                </Badge>
              </div>
              {showBulkPanel ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          
          {showBulkPanel && (
            <CardContent className="p-4">
              <p className="text-sm text-[#6B6B6B] mb-4">
                Select opportunities to run bulk decision maker discovery
              </p>
              
              <div className="max-h-64 overflow-y-auto border border-[#E8E4DE] rounded-lg divide-y divide-[#E8E4DE]">
                {opportunities.map((opp) => (
                  <div 
                    key={opp.id}
                    className={`p-3 flex items-center gap-3 cursor-pointer transition-colors ${
                      selectedOpps.includes(opp.id) ? 'bg-[#B89B5E]/10' : 'hover:bg-[#FAF9F7]'
                    }`}
                    onClick={() => toggleOppSelection(opp.id)}
                    data-testid={`opp-select-${opp.id}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedOpps.includes(opp.id) 
                        ? 'bg-[#B89B5E] border-[#B89B5E]' 
                        : 'border-[#D1D5DB]'
                    }`}>
                      {selectedOpps.includes(opp.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{opp.company_name}</p>
                      <p className="text-xs text-[#6B6B6B]">{opp.stage}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 flex justify-end">
                <Button 
                  onClick={handleBulkDiscovery}
                  disabled={bulkLoading || selectedOpps.length === 0}
                  className="bg-[#B89B5E] hover:bg-[#A08050] text-white"
                  data-testid="bulk-discover-button"
                >
                  {bulkLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Run Discovery ({selectedOpps.length})
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
        
        {/* Suggested Contacts from Previous Discoveries */}
        <Card className="border-[#E8E4DE] bg-white shadow-sm">
          <CardHeader className="border-b border-[#E8E4DE]">
            <CardTitle className="flex items-center gap-2 text-lg font-medium">
              <TrendingUp className="w-5 h-5 text-[#B89B5E]" />
              Discovered Executives
              <Badge variant="outline" className="ml-2">
                {suggestedContacts.length} pending
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loadingSuggested ? (
              <div className="p-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#B89B5E]" />
              </div>
            ) : suggestedContacts.length === 0 ? (
              <div className="p-8 text-center text-[#6B6B6B]">
                <Users className="w-12 h-12 mx-auto mb-3 text-[#E8E4DE]" />
                <p>No pending contacts. Run a discovery to find decision makers.</p>
              </div>
            ) : (
              <div className="divide-y divide-[#E8E4DE]">
                {suggestedContacts.slice(0, 10).map((contact) => (
                  <div 
                    key={contact.id}
                    className="p-4 hover:bg-[#FAF9F7] transition-colors"
                    data-testid={`suggested-contact-${contact.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#F0EDE8] flex items-center justify-center">
                          <span className="text-sm font-medium text-[#B89B5E]">
                            {contact.name?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <h4 className="font-medium text-[#1C1C1C]">{contact.name}</h4>
                          <p className="text-sm text-[#6B6B6B]">{contact.title}</p>
                          <p className="text-xs text-[#9CA3AF]">{contact.company}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${CONFIDENCE_COLORS[contact.confidence] || CONFIDENCE_COLORS.medium}`}>
                          {contact.confidence_score}%
                        </Badge>
                        {contact.linkedin_url && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              openLinkedInProfile(contact.linkedin_url);
                            }}
                            className="p-1.5 rounded hover:bg-[#E8E4DE]"
                            aria-label="Open LinkedIn profile"
                            data-testid={`dm-linkedin-saved-${contact.id}`}
                          >
                            <Linkedin className="w-4 h-4 text-[#0077B5]" />
                          </button>
                        )}
                        <Button 
                          size="sm" 
                          onClick={() => handleConvertContact(contact)}
                          className="bg-[#B89B5E] hover:bg-[#A08050] text-white h-8 px-3"
                          data-testid={`convert-contact-${contact.id}`}
                        >
                          <UserPlus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDismissContact(contact.id)}
                          className="text-[#9CA3AF] hover:text-red-500 h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add to CRM Dialog */}
      <Dialog open={!!addingContact} onOpenChange={(open) => !open && setAddingContact(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#B89B5E]" />
              Add {addingContact?.name} to CRM
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="mb-4 p-3 bg-[#F7F5F2] rounded-lg">
              <p className="font-medium text-[#1C1C1C]">{addingContact?.name}</p>
              <p className="text-sm text-[#6B6B6B]">{addingContact?.title}</p>
              {addingContact?.company && (
                <p className="text-sm text-[#6B6B6B]">{addingContact?.company}</p>
              )}
            </div>
            
            <label className="block text-sm font-medium text-[#1C1C1C] mb-2">
              Select Account/Opportunity *
            </label>
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-full" data-testid="dm-add-account-select">
                <SelectValue placeholder={opportunities.length === 0 ? 'No accounts yet — create one below' : 'Choose an account...'} />
              </SelectTrigger>
              <SelectContent>
                {opportunities.map(opp => (
                  <SelectItem key={opp.id} value={opp.id}>
                    {opp.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Inline "+ Create new account" — solves the case where no
                opportunity exists yet for this prospect's company. */}
            {addingContact?.company && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full mt-2 border-[#B89B5E]/40 text-[#B89B5E] hover:bg-[#B89B5E]/10 justify-start"
                onClick={handleCreateAccountInline}
                disabled={creatingAccount || !!opportunities.find(o => o.company_name === addingContact.company)}
                data-testid="dm-create-new-account-btn"
              >
                {creatingAccount ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account…</>
                ) : opportunities.find(o => o.company_name === addingContact.company) ? (
                  <>✓ Account &ldquo;{addingContact.company}&rdquo; already exists — select above</>
                ) : (
                  <>+ Create new account for &ldquo;{addingContact.company}&rdquo;</>
                )}
              </Button>
            )}
            <p className="text-xs text-[#6B6B6B] mt-2">
              The contact will be associated with this account in your CRM.
            </p>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setAddingContact(null);
                setSelectedAccount('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddDiscoveredContact}
              disabled={!selectedAccount}
              className="bg-[#B89B5E] hover:bg-[#9A8049] text-white"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
